/////////////////////////////////////////////////////////////////////////////////////////////
// Copyright 2026 Garmin International, Inc.
// Licensed under the Flexible and Interoperable Data Transfer (FIT) Protocol License; you
// may not use this file except in compliance with the Flexible and Interoperable Data
// Transfer (FIT) Protocol License.
/////////////////////////////////////////////////////////////////////////////////////////////
// ****WARNING****  This file is auto-generated!  Do NOT edit this file.
// Profile Version = 21.218.0Release
// Tag = production/release/21.218.0-0-g96fee6d7
/////////////////////////////////////////////////////////////////////////////////////////////

package com.garmin.fit

import com.garmin.fit.types.*
import kotlin.time.Instant

/**
 * The FIT `hrv_status_summary` message.
 *
 * A thin typed view over [Mesg]: the accessors below read and write the same
 * numbered fields the untyped API exposes, so nothing is hidden by using them.
 */
public open class HrvStatusSummaryMesg : Mesg {
    public constructor() : super("HrvStatusSummary", Profile.MesgNum.HRV_STATUS_SUMMARY)

    public constructor(mesg: Mesg) : super(mesg)

    internal constructor(mesg: Mesg, adopt: Boolean) : super(mesg, adopt)

    /** timestamp. */
    public var timestamp: Instant?
        get() = (getFieldValue(TIMESTAMP_FIELD_NUM) as? UInt)?.let { FitDateTime.toInstant(it) }
        set(value) = setFieldValue(TIMESTAMP_FIELD_NUM, value?.let { FitDateTime.toTimestamp(it) })

    /** weekly_average, in ms. 7 day RMSSD average over sleep */
    public var weeklyAverage: Double?
        get() = getFieldValue(WEEKLY_AVERAGE_FIELD_NUM) as? Double
        set(value) = setFieldValue(WEEKLY_AVERAGE_FIELD_NUM, value)

    /** last_night_average, in ms. Last night RMSSD average over sleep */
    public var lastNightAverage: Double?
        get() = getFieldValue(LAST_NIGHT_AVERAGE_FIELD_NUM) as? Double
        set(value) = setFieldValue(LAST_NIGHT_AVERAGE_FIELD_NUM, value)

    /** last_night_5_min_high, in ms. 5 minute high RMSSD value over sleep */
    public var lastNight5MinHigh: Double?
        get() = getFieldValue(LAST_NIGHT_5_MIN_HIGH_FIELD_NUM) as? Double
        set(value) = setFieldValue(LAST_NIGHT_5_MIN_HIGH_FIELD_NUM, value)

    /** baseline_low_upper, in ms. 3 week baseline, upper boundary of low HRV status */
    public var baselineLowUpper: Double?
        get() = getFieldValue(BASELINE_LOW_UPPER_FIELD_NUM) as? Double
        set(value) = setFieldValue(BASELINE_LOW_UPPER_FIELD_NUM, value)

    /** baseline_balanced_lower, in ms. 3 week baseline, lower boundary of balanced HRV status */
    public var baselineBalancedLower: Double?
        get() = getFieldValue(BASELINE_BALANCED_LOWER_FIELD_NUM) as? Double
        set(value) = setFieldValue(BASELINE_BALANCED_LOWER_FIELD_NUM, value)

    /** baseline_balanced_upper, in ms. 3 week baseline, upper boundary of balanced HRV status */
    public var baselineBalancedUpper: Double?
        get() = getFieldValue(BASELINE_BALANCED_UPPER_FIELD_NUM) as? Double
        set(value) = setFieldValue(BASELINE_BALANCED_UPPER_FIELD_NUM, value)

    /** status. */
    public var status: HrvStatus?
        get() = (getFieldValue(STATUS_FIELD_NUM) as? UByte)?.let { HrvStatus.fromValue(it) }
        set(value) = setFieldValue(STATUS_FIELD_NUM, value?.value)

    public companion object {
        public const val TIMESTAMP_FIELD_NUM: UByte = 253u
        public const val WEEKLY_AVERAGE_FIELD_NUM: UByte = 0u
        public const val LAST_NIGHT_AVERAGE_FIELD_NUM: UByte = 1u
        public const val LAST_NIGHT_5_MIN_HIGH_FIELD_NUM: UByte = 2u
        public const val BASELINE_LOW_UPPER_FIELD_NUM: UByte = 3u
        public const val BASELINE_BALANCED_LOWER_FIELD_NUM: UByte = 4u
        public const val BASELINE_BALANCED_UPPER_FIELD_NUM: UByte = 5u
        public const val STATUS_FIELD_NUM: UByte = 6u

        /**
         * The profile definition of every field of this message, with no values.
         *
         * Built once when this class is first touched, and handed out only as
         * copies, so the decoder pays for a message type it never meets nothing.
         */
        internal val PROTOTYPE_FIELDS: Map<Int, Field> = buildPrototypeFields()

        private fun buildPrototypeFields(): Map<Int, Field> {
            val fields = LinkedHashMap<Int, Field>()
            fields[253] = Field("Timestamp", 253u, BaseType.UINT32, 1.0, 0.0, "", false)
            fields[0] = Field("WeeklyAverage", 0u, BaseType.UINT16, 128.0, 0.0, "ms", false)
            fields[1] = Field("LastNightAverage", 1u, BaseType.UINT16, 128.0, 0.0, "ms", false)
            fields[2] = Field("LastNight5MinHigh", 2u, BaseType.UINT16, 128.0, 0.0, "ms", false)
            fields[3] = Field("BaselineLowUpper", 3u, BaseType.UINT16, 128.0, 0.0, "ms", false)
            fields[4] = Field("BaselineBalancedLower", 4u, BaseType.UINT16, 128.0, 0.0, "ms", false)
            fields[5] = Field("BaselineBalancedUpper", 5u, BaseType.UINT16, 128.0, 0.0, "ms", false)
            fields[6] = Field("Status", 6u, BaseType.ENUM, 1.0, 0.0, "", false)
            return fields
        }
    }
}
